import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * Tells a long-lived tab when it is running old code, instead of letting it
 * find out by features silently not existing.
 *
 * Operators keep this app open for days. Client-side routing never re-fetches
 * JavaScript, so a tab opened before a deploy runs the old bundle
 * indefinitely — on 2026-07-29 the operator watched three Vercel deploys go
 * "Ready" while their tab kept rendering code from before all of them, and
 * each fix looked like it had done nothing. The server side was verified
 * fresh each time; the tab was the stale half, and nothing told it.
 *
 * Mechanics: the build stamps its git sha into the bundle
 * (REACT_APP_GIT_SHA) and into /version.json. This compares the two on an
 * interval and on window focus — focus because "come back to the tab after a
 * deploy" is exactly the stale moment. /version.json is not under /static/,
 * so the service worker passes it straight to the network, and the fetch
 * carries no-store so no cache layer can answer for it.
 *
 * A banner rather than an automatic reload: the operator may be mid-form
 * taking a payment, and losing that to a surprise reload is worse than being
 * stale for another minute.
 */
const CHECK_EVERY_MS = 5 * 60 * 1000;

const StaleVersionBanner = () => {
  const [staleSha, setStaleSha] = useState(null);
  const mine = process.env.REACT_APP_GIT_SHA;

  useEffect(() => {
    if (!mine) return undefined; // dev server — nothing to compare
    let cancelled = false;

    const check = async () => {
      try {
        const res = await fetch(`/version.json?_=${Date.now()}`, {
          cache: 'no-store',
        });
        if (!res.ok) return;
        const { sha } = await res.json();
        if (!cancelled && sha && sha !== mine) setStaleSha(sha);
      } catch {
        /* offline or transient — say nothing rather than nag */
      }
    };

    check();
    const timer = setInterval(check, CHECK_EVERY_MS);
    window.addEventListener('focus', check);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('focus', check);
    };
  }, [mine]);

  // Self-update when it cannot interrupt anyone: the moment the tab goes to
  // the background with an update pending, reload it there. The operator
  // comes back to a current app without ever pressing anything — the button
  // only exists for the case where they are LOOKING at the page, because a
  // reload under a half-typed payment form is worse than a stale minute.
  // Deploy-heavy days made "keep pressing Update" a chore; this removes it.
  useEffect(() => {
    if (!staleSha) return undefined;
    const onHide = () => {
      if (document.hidden) window.location.reload();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  }, [staleSha]);

  if (!staleSha) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-3 rounded-full bg-slate-900 text-white pl-4 pr-2 py-2 shadow-lg">
      <span className="text-[13px]">A newer version of mySMS360 is available.</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        // Inline color, deliberately OUTSIDE the theme system: the theme layer
        // remaps utility color classes to the operator's accent, which turned
        // this button pale-on-pale. Amber + near-black is legible on the dark
        // pill under every theme, and nothing can restyle an inline value.
        className="inline-flex items-center gap-1.5 rounded-full text-[13px] font-semibold px-3 py-1"
        style={{ backgroundColor: '#f59e0b', color: '#111827' }}
      >
        <RefreshCw className="h-3.5 w-3.5" /> Update now
      </button>
    </div>
  );
};

export default StaleVersionBanner;
